import clsx from 'clsx';
import type { FunctionComponent } from 'react';

import { Button } from '../../core/button/Button';
import type { SideNavigationScope } from './ScopeOrbit';
import { ScopeOrbit } from './ScopeOrbit';
import { SideNavigationTooltip } from './SideNavigationTooltip';
import { useSettledScope } from './use-settled-scope';

const SCOPE_MOTION = 'duration-[400ms] ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none';

type ScopePosition = { readonly height: string; readonly left: string; readonly top: string; readonly width: string };

// Where scope `index` sits in the switch. Open, the pills form one 44px row and
// the current scope's pill takes the room left by the others; folded, they
// stack into 30px circles. Both are calc() positions so the browser can tween
// between them while the rail changes width.
const scopePosition = (index: number, currentIndex: number, count: number, expanded: boolean): ScopePosition => {
    if (!expanded) {
        return { height: '30px', left: '3px', top: `${3 + index * 32}px`, width: 'calc(100% - 6px)' };
    }

    if (index === currentIndex) {
        return {
            height: '32px',
            left: `${6 + index * 34}px`,
            top: '6px',
            width: `calc(100% - ${12 + (count - 1) * 34}px)`,
        };
    }

    return {
        height: '32px',
        left: index < currentIndex ? `${6 + index * 34}px` : `calc(100% - ${6 + (count - index) * 34 - 2}px)`,
        top: '6px',
        width: '32px',
    };
};

export type ScopeSwitchOption = {
    readonly label: string;
    readonly scope: SideNavigationScope;
};

export const ScopeSwitch: FunctionComponent<{
    readonly ariaLabel: string;
    readonly current: SideNavigationScope;
    readonly expanded: boolean;
    readonly onSelect: (scope: SideNavigationScope) => void;
    readonly options: readonly ScopeSwitchOption[];
}> = ({ ariaLabel, current, expanded, onSelect, options }) => {
    const currentIndex = Math.max(
        options.findIndex((option) => option.scope === current),
        0,
    );
    const settledScope = useSettledScope(current);

    return (
        <div className="shrink-0 pb-xl">
            <fieldset
                aria-label={ariaLabel}
                className={clsx(
                    'surface-recessed-pocket-pressed relative m-0 w-full rounded-full border-0 p-0 font-sans transition-[height]',
                    SCOPE_MOTION,
                )}
                style={{ height: expanded ? 44 : 6 + options.length * 32 - 2 }}
            >
                <span
                    aria-hidden={true}
                    className={clsx(
                        'pointer-events-none absolute rounded-full bg-surface-raised shadow-sm transition-[left,top,width,height]',
                        SCOPE_MOTION,
                    )}
                    style={scopePosition(currentIndex, currentIndex, options.length, expanded)}
                />
                {options.map((option, index) => {
                    const isCurrent = index === currentIndex;

                    return (
                        <SideNavigationTooltip key={option.scope} expanded={expanded} label={option.label}>
                            <Button
                                aria-label={option.label}
                                aria-pressed={isCurrent}
                                className={clsx(
                                    'absolute flex items-center justify-center overflow-hidden rounded-full border-0 bg-transparent p-0 outline-none focus-visible:outline-offset-0!',
                                    'transition-[left,top,width,height]',
                                    SCOPE_MOTION,
                                    isCurrent && 'font-medium text-text-content-strong text-body-xs',
                                )}
                                data-scope={option.scope}
                                style={scopePosition(index, currentIndex, options.length, expanded)}
                                variant="plain"
                                onClick={() => onSelect(option.scope)}
                            >
                                <ScopeOrbit
                                    current={isCurrent}
                                    scope={option.scope}
                                    settled={option.scope === settledScope}
                                />
                                {isCurrent ? (
                                    <span
                                        className={clsx(
                                            'truncate transition-[max-width,margin,opacity] ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none',
                                            expanded
                                                ? 'ml-[7px] max-w-40 opacity-100 duration-[400ms]'
                                                : 'ml-0 max-w-0 opacity-0 duration-150',
                                        )}
                                    >
                                        {option.label}
                                    </span>
                                ) : null}
                            </Button>
                        </SideNavigationTooltip>
                    );
                })}
            </fieldset>
            <span
                aria-hidden={true}
                className={clsx(
                    'mx-auto block h-px w-6 bg-border-subtle transition-[margin,opacity]',
                    SCOPE_MOTION,
                    expanded ? 'mt-0 opacity-0' : 'mt-xl opacity-100',
                )}
            />
        </div>
    );
};
